import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Star, Sparkles, Crown, Palette, Gem } from 'lucide-react';
import { DjaySpecimen, DjayVariant, DjayAccessory, DjayTier, DjayTreatment, MascotRarity, TreatmentUnlockInfo } from '../../mascot';
import { VariantUnlockInfo } from '../../mascot/useVariantUnlocks';
import { AccessoryUnlockInfo, TierUnlockInfo, ACCESSORY_CATEGORY_LABELS, AccessoryCategory } from '../../mascot/useDjayCustomization';
import { RADIUS } from '../../config/designTokens';
type CustomizationTab = 'looks' | 'accessories' | 'tiers' | 'finishes';
/**
 * CollectionLocker — the learner-facing Djay locker/customizer.
 *
 * Renamed from `DjayShowcase` (MASCOT_COLLECTION_DDD P2a) to remove the
 * duplicate-symbol collision with the admin-only `mascot/DjayShowcase.tsx`
 * QA configurator. This is the look-PICKER shown on the Collection page; the
 * admin one inspects the raw render-primitive matrix.
 */
interface CollectionLockerProps {
  variants: VariantUnlockInfo[];
  selectedVariant: DjayVariant;
  onSelectVariant: (variantId: DjayVariant) => void;
  accessories: AccessoryUnlockInfo[];
  selectedAccessory: DjayAccessory;
  onSelectAccessory: (accessoryId: DjayAccessory) => void;
  tiers: TierUnlockInfo[];
  selectedTier: DjayTier;
  onSelectTier: (tierId: DjayTier) => void;
  treatments: TreatmentUnlockInfo[];
  selectedTreatment: DjayTreatment;
  onSelectTreatment: (treatmentId: DjayTreatment) => void;
  focusedVariant: string | null;
  rarityStyles: Record<MascotRarity, {
    border: string;
    bg: string;
    text: string;
    glow: string;
    label: string;
  }>;
}
export function CollectionLocker({
  variants,
  selectedVariant,
  onSelectVariant,
  accessories,
  selectedAccessory,
  onSelectAccessory,
  tiers,
  selectedTier,
  onSelectTier,
  treatments,
  selectedTreatment,
  onSelectTreatment,
  focusedVariant,
  rarityStyles
}: CollectionLockerProps) {
  const [activeTab, setActiveTab] = useState<CustomizationTab>('looks');
  const activeVariantInfo = variants.find(v => v.id === selectedVariant);
  const activeAccessoryInfo = accessories.find(a => a.id === selectedAccessory);
  const activeTierInfo = tiers.find(t => t.id === selectedTier);
  const activeTreatmentInfo = treatments.find(t => t.id === selectedTreatment);
  return <div className={`bg-white p-6 sm:p-8 ${RADIUS.card} border-2 border-slate-100 shadow-sm flex flex-col lg:flex-row gap-8 lg:gap-12 items-center lg:items-start`} data-id="element-1150" data-mp="3rxo6e6ag9njd">
      {/* Left: Hero Djay */}
      <div className="flex flex-col items-center gap-4 w-full lg:w-1/3" data-id="element-1151" data-mp="3469247z44w5h">
        <div className="relative w-48 h-48 sm:w-56 sm:h-56 flex-shrink-0 flex items-center justify-center bg-gradient-to-b from-orange-50 to-amber-50 rounded-full border-4 border-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-id="element-1152" data-mp="348c0bfaf5vla">
          <motion.div key={`${selectedVariant}-${selectedAccessory}-${selectedTier}-${selectedTreatment}`} initial={{
          scale: 0.8,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          bounce: 0.5,
          duration: 0.6
        }} className="transform scale-[1.8] mt-8" data-id="element-1153" data-mp="of381s19og3k">
            <DjaySpecimen look={{
            variant: selectedVariant,
            accessory: selectedAccessory,
            tier: selectedTier,
            treatment: selectedTreatment
          }} size="xl" emotion="excited" animate={true} data-id="element-1154" data-mp="1p9err5xnelf5" />
          </motion.div>
        </div>
        <div className="text-center" data-id="element-1155" data-mp="3amhiawbq8equ">
          <h3 className="text-xl font-black text-slate-800" data-id="element-1156" data-mp="3alg9kyi6o4ca">
            {activeVariantInfo?.name || 'Jouw Djay'}
          </h3>
          <div className="flex flex-col items-center gap-1 mt-1" data-id="element-1157" data-mp="35o7mcji6ha46">
            {activeAccessoryInfo && activeAccessoryInfo.id !== 'none' && <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-1158" data-mp="27c3qwjf4jbbr">
                + {activeAccessoryInfo.name}
              </span>}
            {activeTierInfo && activeTierInfo.id !== 'rookie' && <span className="text-xs font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1159" data-mp="1y92lkky31o95">
                <Crown className="w-3 h-3" data-id="element-1160" data-mp="1ocpr3kupr6dc" /> {activeTierInfo.name}
              </span>}
            {activeTreatmentInfo && activeTreatmentInfo.id !== 'none' && <span className="text-xs font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1161" data-mp="1aj8944hoe1pf">
                <Gem className="w-3 h-3" data-id="element-1162" data-mp="10nf02v360qce" /> {activeTreatmentInfo.name}
              </span>}
          </div>
        </div>
      </div>

      {/* Right: Customizer */}
      <div className="flex-1 w-full flex flex-col gap-4" data-id="element-1163" data-mp="15ocj19w01oey">
        {/* Tabs */}
        <div className="flex p-1 bg-slate-100 rounded-xl" data-id="element-1164" data-mp="m0q24xokal84">
          <button onClick={() => setActiveTab('looks')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'looks' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1165" data-mp="hzmmab6ikp6w">
            <Palette className="w-4 h-4" data-id="element-1166" data-mp="14yiwvcp17ouf" />
            Looks
          </button>
          <button onClick={() => setActiveTab('accessories')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'accessories' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1167" data-mp="10xyzq94gkl8a">
            <Sparkles className="w-4 h-4" data-id="element-1168" data-mp="1omi2k94qo5sz" />
            Accessoires
          </button>
          <button onClick={() => setActiveTab('tiers')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'tiers' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1169" data-mp="1joqc7jjqsrqw">
            <Crown className="w-4 h-4" data-id="element-1170" data-mp="20pazrbrdkac9" />
            Rang
          </button>
          <button onClick={() => setActiveTab('finishes')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'finishes' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1171" data-mp="25g16asbjej8j">
            <Gem className="w-4 h-4" data-id="element-1172" data-mp="2jfkflog340mf" />
            Finish
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-1173" data-mp="2oewyjy3u4njq">
          {activeTab === 'looks' && <motion.div key="looks" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-4" data-id="element-1174" data-mp="2ocswzmtes0mm">
              <div className="flex items-center justify-between mb-2" data-id="element-1175" data-mp="csblwanu7ld1">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1176" data-mp="7ti78ewtdoba">
                  <Palette className="w-4 h-4 text-amber-500" data-id="element-1177" data-mp="15g7fh6i40x5l" />
                  Kies je look
                </h4>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1178" data-mp="10h4kkevug7bc">
                  {variants.filter(v => v.unlocked).length} /{' '}
                  {variants.length} ontgrendeld
                </span>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 sm:gap-4" data-id="element-1179" data-mp="qlkuwa7vi4v1">
                {variants.map(card => {
              const isSelected = selectedVariant === card.id;
              const isFocused = focusedVariant === card.id;
              const styles = rarityStyles[card.rarity];
              return <motion.button key={card.id} id={`variant-${card.id}`} whileHover={card.unlocked && !isSelected ? {
                scale: 1.05
              } : {}} whileTap={card.unlocked && !isSelected ? {
                scale: 0.95
              } : {}} onClick={() => {
                if (card.unlocked && !isSelected) onSelectVariant(card.id);
              }} className={`
                        relative aspect-square rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all
                        ${card.unlocked ? `${styles.bg} ${styles.border} cursor-pointer` : 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed'}
                        ${isSelected ? 'ring-4 ring-orange-400 ring-offset-2 shadow-md' : ''}
                        ${isFocused ? 'ring-4 ring-amber-400 ring-offset-2 animate-pulse' : ''}
                      `} title={card.name} aria-label={card.unlocked ? `Kies ${card.name}` : `Vergrendeld: ${card.name}`} data-id="element-1180" data-mp="9s30okni5rbk">
                      {!card.unlocked && <div className="absolute top-2 right-2 z-20" data-id="element-1181" data-mp="2i19klzvflmwn">
                          <div className="w-5 h-5 rounded-full bg-slate-200/80 backdrop-blur-sm flex items-center justify-center" data-id="element-1182" data-mp="267kwv3udj5v">
                            <Lock className="w-3 h-3 text-slate-500" data-id="element-1183" data-mp="3tgemx2rnhjfq" />
                          </div>
                        </div>}

                      <div className={`flex items-center justify-center ${!card.unlocked ? 'grayscale opacity-60' : ''}`} data-id="element-1184" data-mp="3n7w5vgii7g2k">
                        {card.rarity === 'secret' && !card.unlocked ? <span className="text-2xl font-black text-slate-300 block" data-id="element-1185" data-mp="3dexexrzjbdfs">
                            ???
                          </span> : <DjaySpecimen look={{
                    variant: card.id
                  }} size="xl" emotion={card.unlocked ? isSelected ? 'excited' : 'happy' : 'sleeping'} animate={false} data-id="element-1186" data-mp="34esc7v5ad37y" />}
                      </div>

                      {card.unlocked && <div className={`absolute bottom-0 left-0 right-0 h-1.5 ${styles.bg.replace('bg-', 'bg-').replace('50', '300')}`} data-id="element-1187" data-mp="19zlhfnyliaxh" />}
                    </motion.button>;
            })}
              </div>

              {/* Selected/Focused Variant Details */}
              <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1188" data-mp="mc3uc5qa010u">
                {variants.map(v => {
              const showDetails = focusedVariant ? v.id === focusedVariant : v.id === selectedVariant;
              if (!showDetails) return null;
              return <motion.div key={v.id} initial={{
                opacity: 0,
                y: 5
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex flex-col gap-2" data-id="element-1189" data-mp="27mkdbwhgreub">
                      <div className="flex items-center gap-2" data-id="element-1190" data-mp="ck76y6zbd1qe">
                        <span className="font-black text-slate-700" data-id="element-1191" data-mp="3l0a7lss0dq58">
                          {v.name}
                        </span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${rarityStyles[v.rarity].bg} ${rarityStyles[v.rarity].text}`} data-id="element-1192" data-mp="3g28oj7ul1qtr">
                          {rarityStyles[v.rarity].label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500" data-id="element-1193" data-mp="31aqc1k0xmef5">{v.description}</p>

                      {!v.unlocked && v.progress.max > 0 && <div className="mt-2" data-id="element-1194" data-mp="2n6res4uub4d9">
                          <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1195" data-mp="2n5zhmpllgykv">
                            <span data-id="element-1196" data-mp="2rz0yv499xa7m">{v.condition}</span>
                            <span data-id="element-1197" data-mp="2d8tmn69v0nfd">
                              {v.progress.current} / {v.progress.max}
                            </span>
                          </div>
                          <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1198" data-mp="2i5br051a2jr6">
                            <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${v.progress.current / v.progress.max * 100}%`
                    }} transition={{
                      duration: 0.5
                    }} data-id="element-1199" data-mp="35v666t92kkel" />
                          </div>
                        </div>}
                    </motion.div>;
            })}
              </div>
            </motion.div>}

          {activeTab === 'accessories' && <motion.div key="accessories" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-4" data-id="element-1200" data-mp="1vxzk4ypidxv9">
              <div className="flex items-center justify-between mb-2" data-id="element-1201" data-mp="29r7c4rhi4vdh">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1202" data-mp="13bwwth1xt4a6">
                  <Sparkles className="w-4 h-4 text-amber-500" data-id="element-1203" data-mp="yctuw5h97e6f" />
                  Kies je accessoire
                </h4>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1204" data-mp="3a0ymng768ldb">
                  {accessories.filter(a => a.unlocked).length} /{' '}
                  {accessories.length} ontgrendeld
                </span>
              </div>

              <div className="flex flex-col gap-8" data-id="element-1205" data-mp="3oojoz7w82gk6">
                {/* Geen accessoire optie */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-1206" data-mp="3to6d4cdl9h7r">
                  {accessories.filter(a => a.id === 'none').map(acc => {
                const isSelected = selectedAccessory === acc.id;
                return <motion.button key={acc.id} whileHover={!isSelected ? {
                  scale: 1.02
                } : {}} whileTap={!isSelected ? {
                  scale: 0.98
                } : {}} onClick={() => {
                  if (!isSelected) onSelectAccessory(acc.id);
                }} className={`
                          relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all text-center
                          bg-white border-slate-200 cursor-pointer hover:border-amber-300
                          ${isSelected ? 'ring-4 ring-amber-400 ring-offset-2 shadow-md border-amber-300' : ''}
                        `} data-id="element-1207" data-mp="bitcypnq07us">
                          <div className="h-32 sm:h-36 w-full flex items-center justify-center mb-3 rounded-xl bg-slate-50" data-id="element-1208" data-mp="37tfrh1axoo7t">
                            <span className="text-5xl" data-id="element-1209" data-mp="3mhzdglw3v1ke">🚫</span>
                          </div>
                          <span className="font-black text-slate-800 text-base mb-0.5" data-id="element-1210" data-mp="3qjufjslv1iz8">
                            {acc.name}
                          </span>
                        </motion.button>;
              })}
                </div>

                {/* Groepeer per categorie */}
                {(Object.keys(ACCESSORY_CATEGORY_LABELS) as AccessoryCategory[]).map(category => {
              const categoryAccessories = accessories.filter(a => a.category === category);
              if (categoryAccessories.length === 0) return null;
              return <div key={category} className="space-y-3" data-id="element-1211" data-mp="2jvnq1fbf82en">
                      <h5 className="font-bold text-slate-400 uppercase tracking-wider text-xs border-b-2 border-slate-100 pb-2" data-id="element-1212" data-mp="2jw76erlfaiqk">
                        {ACCESSORY_CATEGORY_LABELS[category]}
                      </h5>
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-1213" data-mp="34ans1k5urr51">
                        {categoryAccessories.map(acc => {
                    const isSelected = selectedAccessory === acc.id;
                    return <motion.button key={acc.id} whileHover={acc.unlocked && !isSelected ? {
                      scale: 1.02
                    } : {}} whileTap={acc.unlocked && !isSelected ? {
                      scale: 0.98
                    } : {}} onClick={() => {
                      if (acc.unlocked && !isSelected) onSelectAccessory(acc.id);
                    }} className={`
                                relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all text-center
                                ${acc.unlocked ? 'bg-white border-slate-200 cursor-pointer hover:border-amber-300' : 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed'}
                                ${isSelected ? 'ring-4 ring-amber-400 ring-offset-2 shadow-md border-amber-300' : ''}
                              `} data-id="element-1214" data-mp="2qc7d55xu6ubt">
                              {!acc.unlocked && <div className="absolute top-3 right-3 z-20" data-id="element-1215" data-mp="m2cf4wzfhqer">
                                  <div className="w-6 h-6 rounded-full bg-slate-200/80 backdrop-blur-sm flex items-center justify-center" data-id="element-1216" data-mp="r1yxtly0zyuy">
                                    <Lock className="w-3 h-3 text-slate-500" data-id="element-1217" data-mp="2jal6gnurec2j" />
                                  </div>
                                </div>}

                              <div className={`h-32 sm:h-36 w-full flex items-center justify-center mb-3 rounded-xl bg-gradient-to-b from-orange-50/60 to-amber-50/60 ${!acc.unlocked ? 'grayscale opacity-60' : ''}`} data-id="element-1218" data-mp="2tabz0jzregzz">
                                <DjaySpecimen look={{
                          variant: 'default',
                          accessory: acc.id
                        }} size="hero" emotion={isSelected ? 'excited' : 'happy'} animate={false} data-id="element-1219" data-mp="25in18o4650bn" />
                              </div>

                              <span className="font-black text-slate-800 text-base mb-0.5" data-id="element-1220" data-mp="1ebodbpcqkxvb">
                                {acc.name}
                              </span>
                              <span className="text-xs font-medium text-slate-500" data-id="element-1221" data-mp="19b0lj1good01">
                                {acc.condition}
                              </span>

                              {!acc.unlocked && acc.progress.max > 0 && <div className="w-full mt-3" data-id="element-1222" data-mp="vdc300a39hy5">
                                  <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1223" data-mp="qmc2ym5oajrf">
                                    <span data-id="element-1224" data-mp="lmr40bqi6bep">Voortgang</span>
                                    <span data-id="element-1225" data-mp="lnirt0duz4uf">
                                      {acc.progress.current} /{' '}
                                      {acc.progress.max}
                                    </span>
                                  </div>
                                  <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1226" data-mp="7n7w9k1lt22x">
                                    <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                            width: 0
                          }} animate={{
                            width: `${acc.progress.current / acc.progress.max * 100}%`
                          }} transition={{
                            duration: 0.5
                          }} data-id="element-1227" data-mp="3u502u4tgtbwy" />
                                  </div>
                                </div>}
                            </motion.button>;
                  })}
                      </div>
                    </div>;
            })}
              </div>
            </motion.div>}

          {activeTab === 'tiers' && <motion.div key="tiers" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-4" data-id="element-1228" data-mp="2p4igbk0v6ktg">
              <div className="flex items-center justify-between mb-2" data-id="element-1229" data-mp="21978r3j9ahtv">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1230" data-mp="2f7x9ct5aazmz">
                  <Crown className="w-4 h-4 text-amber-500" data-id="element-1231" data-mp="2f5uarkq2fy6n" />
                  Kies je rang
                </h4>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1232" data-mp="3qnc7i098bxjo">
                  {tiers.filter(t => t.unlocked).length} / {tiers.length}{' '}
                  ontgrendeld
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1233" data-mp="ls41yv7kbvbi">
                {tiers.map(tier => {
              const isSelected = selectedTier === tier.id;
              return <motion.button key={tier.id} whileHover={tier.unlocked && !isSelected ? {
                scale: 1.02
              } : {}} whileTap={tier.unlocked && !isSelected ? {
                scale: 0.98
              } : {}} onClick={() => {
                if (tier.unlocked && !isSelected) onSelectTier(tier.id);
              }} className={`
                        relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all text-center
                        ${tier.unlocked ? 'bg-white border-slate-200 cursor-pointer hover:border-amber-300' : 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed'}
                        ${isSelected ? 'ring-4 ring-amber-400 ring-offset-2 shadow-md border-amber-300' : ''}
                      `} data-id="element-1234" data-mp="upwejrlwgov7">
                      {!tier.unlocked && <div className="absolute top-3 right-3 z-20" data-id="element-1235" data-mp="3qo5c83lcdt5i">
                          <div className="w-6 h-6 rounded-full bg-slate-200/80 backdrop-blur-sm flex items-center justify-center" data-id="element-1236" data-mp="3lxozymt9cdkq">
                            <Lock className="w-3 h-3 text-slate-500" data-id="element-1237" data-mp="3gzdoy8r87x0x" />
                          </div>
                        </div>}

                      <div className={`h-32 sm:h-36 w-full flex items-center justify-center mb-4 rounded-xl bg-gradient-to-b from-orange-50/60 to-amber-50/60 overflow-hidden ${!tier.unlocked ? 'grayscale opacity-60' : ''}`} data-id="element-1238" data-mp="iagaj1qg713i">
                        <DjaySpecimen look={{
                    variant: 'default',
                    tier: tier.id
                  }} size="hero" emotion={tier.unlocked ? isSelected ? 'proud' : 'happy' : 'sleeping'} animate={false} data-id="element-1239" data-mp="2coaofdertjsd" />
                      </div>

                      <span className="font-black text-slate-800 text-lg mb-1" data-id="element-1240" data-mp="1k0og5rullqz6">
                        {tier.name}
                      </span>
                      <span className="text-xs font-medium text-slate-500" data-id="element-1241" data-mp="3edxjf1wjwg8z">
                        {tier.condition}
                      </span>

                      {!tier.unlocked && tier.progress.max > 0 && <div className="w-full mt-3" data-id="element-1242" data-mp="xsishjtfzj12">
                          <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1243" data-mp="xpmxa38zb8gb">
                            <span data-id="element-1244" data-mp="1cigim5axmisw">Voortgang</span>
                            <span data-id="element-1245" data-mp="17ovtz3v8b16d">
                              {tier.progress.current} / {tier.progress.max}
                            </span>
                          </div>
                          <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1246" data-mp="1giqrpqu0tpbz">
                            <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${tier.progress.current / tier.progress.max * 100}%`
                    }} transition={{
                      duration: 0.5
                    }} data-id="element-1247" data-mp="1vg18nfpfnnf3" />
                          </div>
                        </div>}
                    </motion.button>;
            })}
              </div>
            </motion.div>}

          {activeTab === 'finishes' && <motion.div key="finishes" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-4" data-id="element-1248" data-mp="2ngjlgqhphxxp">
              <div className="flex items-center justify-between mb-2" data-id="element-1249" data-mp="gpabdyvb1ski">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1250" data-mp="gnh4qjkoz8ci">
                  <Gem className="w-4 h-4 text-amber-500" data-id="element-1251" data-mp="216t939gx319n" />
                  Kies je finish
                </h4>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1252" data-mp="1x6j2pvp7q8u9">
                  {treatments.filter(t => t.unlocked).length} /{' '}
                  {treatments.length} ontgrendeld
                </span>
              </div>

              <p className="text-xs text-slate-500 -mt-2" data-id="element-1253" data-mp="1ihwcv01ylyos">
                Finishes zijn glanzende effecten die je verdient door te oefenen
                in het Leerlab. Ze passen op elke look.
              </p>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-1254" data-mp="19gyb6oezu39d">
                {treatments.map(treatment => {
              const isSelected = selectedTreatment === treatment.id;
              const styles = rarityStyles[treatment.rarity];
              return <motion.button key={treatment.id} whileHover={treatment.unlocked && !isSelected ? {
                scale: 1.02
              } : {}} whileTap={treatment.unlocked && !isSelected ? {
                scale: 0.98
              } : {}} onClick={() => {
                if (treatment.unlocked && !isSelected) onSelectTreatment(treatment.id);
              }} className={`
                        relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all text-center
                        ${treatment.unlocked ? `${styles.bg} ${styles.border} cursor-pointer hover:brightness-[0.98]` : 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed'}
                        ${isSelected ? `ring-4 ring-amber-400 ring-offset-2 shadow-md ${styles.glow}` : ''}
                      `} title={treatment.name} aria-label={treatment.unlocked ? `Kies finish ${treatment.name}` : `Vergrendeld: ${treatment.name}`} data-id="element-1255" data-mp="2ywefnqhz30sx">
                      {!treatment.unlocked && <div className="absolute top-3 right-3 z-20" data-id="element-1256" data-mp="1lmsl6gocir7q">
                          <div className="w-6 h-6 rounded-full bg-slate-200/80 backdrop-blur-sm flex items-center justify-center" data-id="element-1257" data-mp="16zpp8efo8qmi">
                            <Lock className="w-3 h-3 text-slate-500" data-id="element-1258" data-mp="1bwzjq8hx3sh7" />
                          </div>
                        </div>}

                      <div className={`h-32 sm:h-36 w-full flex items-center justify-center mb-3 rounded-xl bg-white/50 ${!treatment.unlocked ? 'grayscale opacity-60' : ''}`} data-id="element-1259" data-mp="24ebnelajq18p">
                        <DjaySpecimen look={{
                    variant: selectedVariant,
                    treatment: treatment.id
                  }} size="hero" emotion={isSelected ? 'excited' : 'happy'} animate={treatment.unlocked} data-id="element-1260" data-mp="29b7hy4z5uxjt" />
                      </div>

                      <div className="flex items-center gap-1.5 mb-0.5" data-id="element-1261" data-mp="1qjesuln7pwhd">
                        <span className="font-black text-slate-800 text-base" data-id="element-1262" data-mp="1ll4slwmms5ow">
                          {treatment.name}
                        </span>
                        {treatment.id !== 'none' && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${styles.bg} ${styles.text}`} data-id="element-1263" data-mp="1ck6shpfbcibl">
                            {styles.label}
                          </span>}
                      </div>
                      <span className="text-xs font-medium text-slate-500" data-id="element-1264" data-mp="3o78skq033j1i">
                        {treatment.condition}
                      </span>

                      {!treatment.unlocked && treatment.progress.max > 0 && <div className="w-full mt-3" data-id="element-1265" data-mp="11rkw2racpag">
                          <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1266" data-mp="30k0qegxycq3p">
                            <span data-id="element-1267" data-mp="34ldvgot80p9k">Voortgang</span>
                            <span data-id="element-1268" data-mp="39jnrt2qolvwr">
                              {treatment.progress.current} /{' '}
                              {treatment.progress.max}{' '}
                              {treatment.progress.label}
                            </span>
                          </div>
                          <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1269" data-mp="2gsxao6i3mj6k">
                            <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${treatment.progress.current / treatment.progress.max * 100}%`
                    }} transition={{
                      duration: 0.5
                    }} data-id="element-1270" data-mp="2ls0ancj81z7o" />
                          </div>
                        </div>}
                    </motion.button>;
            })}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}